/**
 * Canonical MA Account SoR client.
 * My Profile → GET/PUT /me/profile (+ POST /me/profile/upload-url)
 * Company Profile → GET/PATCH /me/account (+ POST /me/account/logo-upload-url)
 *
 * Person avatar = kk-profile (My Profile). Company logo = MasterAccount.companyLogo
 * (Company Profile). Separate SoRs — never copied between each other.
 *
 * Support Mode and normal login use the same paths; JWT subject is authoritative.
 */

import apiService from './api';
import {
  resolveDisplayAvatarUrl,
  resolveAvatarStorageKey,
} from '../utils/profileAvatar';

function unwrapData(payload) {
  if (!payload) return null;
  if (payload.success === false) {
    throw new Error(payload.message || 'Account API error');
  }
  return payload.data != null ? payload.data : payload;
}

export async function fetchMyProfile() {
  const payload = await apiService.makeRequest('/me/profile', { method: 'GET' });
  return unwrapData(payload);
}

export async function saveMyProfile(fields) {
  const body = {};
  if (fields.firstName !== undefined) body.firstName = fields.firstName;
  if (fields.lastName !== undefined) body.lastName = fields.lastName;
  if (fields.phone !== undefined) body.phone = fields.phone;
  if (fields.avatar !== undefined) body.avatar = fields.avatar;
  const payload = await apiService.makeRequest('/me/profile', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return unwrapData(payload);
}

export async function requestProfileAvatarUploadUrl(contentType) {
  const payload = await apiService.makeRequest('/me/profile/upload-url', {
    method: 'POST',
    body: JSON.stringify({ contentType: contentType || 'image/jpeg' }),
  });
  return unwrapData(payload);
}

export async function requestCompanyLogoUploadUrl(contentType) {
  const payload = await apiService.makeRequest('/me/account/logo-upload-url', {
    method: 'POST',
    body: JSON.stringify({ contentType: contentType || 'image/jpeg' }),
  });
  return unwrapData(payload);
}

export async function fetchCompanyAccount() {
  const payload = await apiService.makeRequest('/me/account', { method: 'GET' });
  return unwrapData(payload);
}

export async function saveCompanyAccount(fields) {
  const body = {};
  const map = [
    'billingPhone',
    'address_street',
    'address_city',
    'address_state',
    'address_zipCode',
    'address_country',
    // COMPANY logo key (server-minted, companies/{maId}/…) or null to remove.
    'companyLogo',
  ];
  map.forEach((key) => {
    if (fields[key] !== undefined) body[key] = fields[key];
  });
  const payload = await apiService.makeRequest('/me/account', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return unwrapData(payload);
}

export function mapAccountToCompanyForm(account) {
  const a = account || {};
  return {
    name: a.businessName || '',
    phoneNumber: a.billingPhone || '',
    accountType: a.accountType || '',
    streetAddress: a.address_street || '',
    city: a.address_city || '',
    state: a.address_state || '',
    zipCode: a.address_zipCode || '',
    country: a.address_country || '',
    // Browser-renderable presigned URL only — never a raw S3 key in <img src>.
    logo: a.companyLogoUrl && String(a.companyLogoUrl).trim() ? String(a.companyLogoUrl).trim() : null,
    logoKey: a.companyLogo || null,
  };
}

export function mapProfileToForm(profile) {
  const p = profile || {};
  // Display URL must be browser-fetchable (presigned avatarUrl or http Auth0
  // picture). Never put a raw private S3 key into <img src>.
  return {
    firstName: p.firstName || '',
    lastName: p.lastName || '',
    email: p.email || '',
    phoneNumber: p.phone || '',
    avatar: resolveDisplayAvatarUrl(p),
    avatarKey: resolveAvatarStorageKey(p),
  };
}
